import { DEBUG } from '@src/DEBUG';
DEBUG.open ( '/xs/service/application-events' );
import {Injectable} from '@angular/core';
import {Constants} from '../../../commons/Constants';
import { XsIdentityInterface } from '../../interfaces/identify.interface';
import { XsEventListenerInterface } from '../../interfaces/event-listener.interface';
import { XsEventify } from './eventify.class';
import { CoreOntology, GisOntology, PluginsOntology, SystemOntology } from '../../ontologies/ontologies';
import * as _ from 'lodash';


@Injectable({
    providedIn: 'root'
})
export class XsApplicationEventsService extends XsEventify implements XsIdentityInterface, XsEventListenerInterface {

    __ID      = null;
    __EVENTS  = null;

    // for debugging
    debug:boolean = Constants.debug_off;

    // these are recognised important global events
    static supportedEvents:any = [ 'photosChange' ];

    ontology:any = null;

    xso:any = {
        namespace: "The current file's namespace",
        namespaces: "The namespaces we define",
        shorthand: "If a given namespace is to be interpreted without a namespace token",
        schema: "The schema we understand",
        description: "A description of an assertion",
        label: "The label of an assertion",
        cardinality: "The cardinality of an assertion",
    };
    // xso:string[] = Object.keys(this.xsoDefinitions);

    
    constructor () {

        super ( XsApplicationEventsService.supportedEvents );

        this.__ID = XsIdentityInterface.__init(this);
        this.__EVENTS = XsEventListenerInterface.__init(this);

        DEBUG.log ( '/xs/service/application-events' );

        this.registerGlobalEvents();

        this.buildOntologies();
    }
    
    register ( id:string, obs:any, onlyRoot:boolean = XsEventify.REGISTER_ONLY_ONE, source:any = null ) {
        // if ( this.debug ) { console.log ( '%c '+this.parentClass+' :: register()', this.debugStyle, id, obs, source ); }
        this._register_external_event ( id, obs, onlyRoot, source );
    }

    // clear stuff
    triggerInit ( id:string, obj:any = null, source:any = null ) {
        this.trigger ( id, 'Init', obj, source );
    }
    // we're starting the process
    triggerStart ( id:string, obj:any = null, source:any = null ) {
        this.trigger ( id, 'Start', obj, source );
    }
    // we know what we have to deal with
    triggerProcess ( id:string, obj:any = null, source:any = null ) {
        this.trigger ( id, 'Process', obj, source );
    }
    // we sometimes tell on what we're doing
    triggerDuring ( id:string, obj:any = null, source:any = null ) {
        this.trigger ( id, 'During', obj, source );
    }
    // we're done doing it
    triggerEnd ( id:string, obj:any = null, source:any = null ) {
        this.trigger ( id, 'End', obj, source );
    }

    triggerReady ( id:string, obj:any = null, source:any = null ) {
        this.trigger ( id, 'Ready', obj, source );
    }

    triggerError ( id:string, obj:any = null, source:any = null ) {
        this.trigger ( id, 'Error', obj, source );
    }

    trigger ( id:string, what:string = '', obj:any = null, source:any = null ) {
        // if ( this.debug ) { console.log ( '%c '+this.parentClass+' :: trigger()', this.debugStyle, id, what, obj, source ); }
        this._e ( id, what, obj, source );
    }

    registerGlobalEvents() {


        // from map.service.ts
        // NOTE : Due to Angular2 circular dependencies we can't define them here, so they are still defined there



        // from feature-detector.service.ts

        this.register ( 'tilesLoading', null, XsEventify.REGISTER_ALL, this.__ID );



        // from map-viewer.service.ts

        // passing in a null here creates a new Subject for us
        this.register ( 'mapViewer', null, XsEventify.REGISTER_ALL, this.__ID );
        this.register ( 'mapViewerDestroy', null, XsEventify.REGISTER_ALL, this.__ID );



        // from scene-controller.service.ts

        // we're in charge of the scene changing (but still using mapService for legacy sake)
        this.register ( 'sceneChange', null, XsEventify.REGISTER_ALL, this.__ID );

        // drawing transforms
        this.register ( 'plotTransform', null, XsEventify.REGISTER_ALL, this.__ID );
        this.register ( 'unplotTransform', null, XsEventify.REGISTER_ALL, this.__ID );

        // drawing annotations
        this.register ( 'plotAnnotation', null, XsEventify.REGISTER_ALL, this.__ID );
        this.register ( 'unplotAnnotation', null, XsEventify.REGISTER_ALL, this.__ID );

    }

    buildOntologies() {

        // console.log ( '## ONTOLOGIES START', this.xso );

        if ( !this.ontology ) {
            this.ontology = {
                _ontologies: {}
            };
        }

        // let raw = { ...CoreOntology, ...GisOntology, ...SystemOntology, ...PluginsOntology };
        // this.ontology = raw;

        // our main ontology files
        [ CoreOntology, GisOntology, SystemOntology, PluginsOntology ].forEach ( o => {
            this.parseOntology(o);
        });

        let lut = {};

        // first pass; pick out entities to have multiples
        Object.entries(this.ontology).forEach(([key, value]) => {
            Object.keys(value).forEach ( k => {
                if ( k == 'cardinality' ) {
                    lut[key] = value[k];
                }
            });
        });

        // pass two; unpack and structure
        Object.entries(this.ontology).forEach(([key, value]) => {
            Object.keys(value).forEach ( k => {
                if ( lut[k] ) {
                    if ( lut[k] == '*' || lut[k] == '*?'  || lut[k] == '1?' ) {
                        let newValues = {};
                        const bits = (''+value[k]).split(' ');
                        bits.forEach ( bit => {
                            newValues[bit] = bit;
                        });
                        const these = Object.keys(newValues);
                        if ( these && these.length ) {
                            if ( !this.ontology[k]['_idx'] ) {
                                this.ontology[k]['_idx'] ={};
                            }
                            these.forEach( nk => {
                                if ( !this.ontology[k]['_idx'][nk] ) {
                                    this.ontology[k]['_idx'][nk] = {};
                                }
                                this.ontology[k]['_idx'][nk][key] = key;
                            });
                            this.ontology[key][k] = newValues;
                        }
                    }
                } 
            });
        });

        console.log ( '##ont', this.ontology  );

        // test cases
        let test = null;

        // all expanded-events
        // test = this.queryTyped('core:isA','sys:expanded-event');
        // console.log ( '##ONTOLOGY TEST all expanded-events', test );

        // all real objects
        // test = this.queryTyped('core:existanceIs','core:real');
        // console.log ( '##ONTOLOGY TEST all real items', test );

        // all plugins
        // test = this.queryTyped('core:isA','sys:plugin');
        // console.log ( '##ONTOLOGY TEST all plugins', test );

        // all visual plugins
        // test = this.queryTypedParent('core:isA','sys:plugin','plugins:visual');
        // console.log ( '##ONTOLOGY TEST visual plugins', test );

    }

    getEntity(key:string) {
        return this.ontology[key];
    }

    queryTyped( type:string, subtype:string ) {
        // pure types can use the in-built indexes
        let ret = [];
        if ( this.ontology[type] && this.ontology[type]['_idx'] ) {
            if ( this.ontology[type]['_idx'][subtype] ) {
                Object.keys(this.ontology[type]['_idx'][subtype]).forEach( e => ret.push(this.getEntity(e)));
            }
        }
        return ret;
    }

    queryTypedParent( type:string, subtype:string, parent:string ) {
        // typed parents needs to iterate and check the _parent (existence and value)
        let ret = [];
        Object.entries(this.ontology).forEach(([key, value]) => {
            if ( value[type] && value[type][subtype] && value['_parent'] && value['_parent'] == parent ) {
                ret.push(value);
            }
        });
        return ret;
    }

    totalCount:number = 0;

    parseOntology ( o:any, state:any = null ) {

        if ( !state ) {
            state = {
                namespace: 'none', 
                level: 0,
                cursor:null,
                parent:null,
                shorthand:null,
                alsoChildrenType:null
            }
            if ( o['namespace'] ) {
                state.namespace = o['namespace'];
                state.cursor = '_'+state.namespace;
                state.parent = '_'+state.namespace;
                // console.log('## ont NAMESPACE', state.namespace );
            }
            if ( o['shorthand'] ) {
                state.shorthand = o['shorthand'];
                // console.log('## ont SHORTHAND', state.shorthand );
            }
        }

        // let levelCount:number = 0;

        Object.entries(o).forEach(([key, value]) => {

            let type = typeof value;

            // do something with key and val

            if ( type == 'object' && key !== 'namespaces' ) {

                // console.log('#ont obj', key);

                let newState = _.cloneDeep(state);
                // let s = _.cloneDeep(state);

                // generic entry for this token
                this.ontology[key] = { _key: key, _n:this.totalCount++ }; //  _key: key, _state:s, _newState:newState, _parent_test: s.parent ? true : false, _parent_test_value: s.parent };

                newState.parent = newState.cursor;
                newState.cursor = key;
                newState.level++;

                if ( newState.alsoChildrenType ) {

                    let v = value[newState.alsoChildrenType.key] ? value[newState.alsoChildrenType.key] : '';
                    v = v + newState.alsoChildrenType.value;

                    // does next level already have this key?
                    if ( value[newState.alsoChildrenType.key] ) {
                        // merge it in
                        value[newState.alsoChildrenType.key] = value[newState.alsoChildrenType.key] + ' ' + newState.alsoChildrenType.value;
                        // console.log('#ont also MERGE',newState.alsoChildrenType, Object.values(value) );
                    } else {
                        // just copy it in
                        value[newState.alsoChildrenType.key] = newState.alsoChildrenType.value;
                        // console.log('#ont also COPY',newState.alsoChildrenType, Object.values(value) );
                    }
                }

                this.parseOntology ( value, newState );

            } else {

                // console.log('#ont prop ['+state.cursor+']', key);

                // is the key part of the XSO ontology definition language schema?
                if ( this.xso[key] ) {

                    if ( !this.ontology[state.cursor] ) {
                        this.ontology[state.cursor] = {};
                    }

                    // properties for the current cursor position
                    this.ontology[state.cursor][key] = value;

                } else {

                    let finalKey = null;

                    // Namespaced?
                    if ( key.indexOf(':') !== -1 ) {

                        finalKey = key;

                        // properties for the current cursor position
                        // this.ontology[state.cursor][key] = value;

                    } else {

                        // Append key with shorthand if there is one
                        if ( state.shorthand ) {

                            finalKey = state.shorthand+':'+key;
                            // properties for the current cursor position
                            // this.ontology[state.cursor][state.shorthand+':'+key] = value;

                        } else {
                            // shouldn't happen; no namespace nor shorthand nor in the xso schema
                        }

                    }

                    // special ontology instruction of inherited type
                    if ( (''+value).indexOf('core:also-all-children') !== -1 ) {
                        const o = { key:key, value:value };
                        // console.log('#ont also',o);
                        state.alsoChildrenType = o;
                    }

                    // special ontology instruction of excluded children type
                    if ( (''+value).indexOf('core:only-all-children') !== -1 ) {

                        // replace the value so the inheritence works for all children
                        value = (''+value).split('core:only-all-children').join('core:also-all-children');

                        const o = { key:key, value:value };
                        
                        state.alsoChildrenType = o;
                        // don't inject this type
                        finalKey = null;
                        
                        // console.log('#ont only',o, value);
                    }

                    if ( finalKey ) {
                        // levelCount++;
                        this.ontology[state.cursor][finalKey] = value;
                    }

                }

                // console.log('## ont ['+state.namespace+']:['+state.cursor+'] ('+state.level+') %c '+key+' ['+type+'] ', 'background-color:#ddd;', value, this.xso[key] ? true : false );

            }
        });

        if ( state.parent && this.ontology[state.cursor] ) {
            this.ontology[state.cursor]['_parent'] = _.clone(state.parent);
        }
    }
}